<template>
  <div class="dicom-viewer">
    <div v-if="noImage" class="dicom-viewer-message">
      <i class="fa fa-circle-info fa-2x mb-3"></i>
      <p class="mb-1 font-weight-bold">This file has no image to display</p>
      <p class="small mb-0">
        It's a DICOM record with header information only (e.g. a structured
        report or RT plan) — there's no scan to render.
      </p>
    </div>

    <div v-else-if="error" class="dicom-viewer-message">
      <i class="fa fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-1 font-weight-bold">This scan couldn't be displayed</p>
      <p class="small mb-0">{{ error }}</p>
    </div>

    <template v-else>
      <div v-if="loading" class="dicom-viewer-message">
        <i class="fa fa-circle-notch fa-spin fa-2x mb-3"></i>
        <p class="small mb-0">Loading scan…</p>
      </div>

      <div
        ref="viewport"
        class="dicom-viewport"
        :class="{ 'is-hidden': loading }"
        @contextmenu.prevent
      ></div>

      <div v-if="!loading" class="dicom-toolbar">
        <div class="dicom-mode-group" role="group" aria-label="Annotation mode">
          <button
            v-for="mode in modes"
            :key="mode.name"
            type="button"
            class="btn btn-sm"
            :class="
              activeTool === mode.name ? 'btn-light' : 'btn-outline-light'
            "
            @click="setActiveTool(mode.name)"
          >
            <i :class="['fa', mode.icon, 'mr-1']"></i>{{ mode.label }}
          </button>
        </div>

        <div
          class="dicom-color-group"
          role="group"
          aria-label="Annotation color"
        >
          <button
            v-for="color in colors"
            :key="color"
            type="button"
            class="dicom-color-swatch"
            :class="{ 'is-active': activeColor === color }"
            :style="{ backgroundColor: color }"
            :aria-label="`Set annotation color to ${color}`"
            title="Color for new annotations · also recolors a selected shape"
            @click="setColor(color)"
          ></button>
        </div>

        <span class="dicom-hint">
          {{ hintText }}
          <template v-if="frameCount > 1"> · ←/→ to change frame</template>
        </span>
        <span v-if="frameCount > 1" class="dicom-frame-counter">
          Frame {{ frameIndex + 1 }} / {{ frameCount }}
        </span>

        <div class="dicom-action-group">
          <button
            class="btn btn-sm btn-outline-light"
            :disabled="!canUndo"
            title="Undo last stroke (Ctrl+Z)"
            @click="undoLast"
          >
            <i class="fa fa-clock-rotate-left mr-1"></i> Undo
          </button>
          <button
            class="btn btn-sm btn-outline-light"
            title="Click a shape then press Delete/Backspace to erase just that one"
            @click="clearAnnotations"
          >
            <i class="fa fa-eraser mr-1"></i> Clear all
          </button>
          <button class="btn btn-sm btn-outline-light" @click="exportSnapshot">
            <i class="fa fa-download mr-1"></i> Save PNG
          </button>
          <button class="btn btn-sm btn-outline-light" @click="reset">
            <i class="fa fa-rotate-left mr-1"></i> Reset
          </button>
        </div>

        <!-- Export failures used to be console-only, which read as a dead button
             on iOS. Deliberately not `error`: that replaces the whole viewer. -->
        <span v-if="exportNotice" class="dicom-export-notice">
          {{ exportNotice }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { DICOM_NO_IMAGE, DICOM_UNREADABLE, inspectDicom } from "@/utils/dicom";

/**
 * Renders a DICOM study in the browser with Cornerstone3D (CAB-357).
 *
 * Reads the file from its existing document URL, so permissions and expiry are
 * unchanged and no second copy of the pixels is stored.
 */
const RENDERING_ENGINE_ID = "c3-dicom-engine";
const VIEWPORT_ID = "c3-dicom-viewport";
const TOOL_GROUP_ID = "c3-dicom-tools";
// Distinct from every color swatch, so a hovered/selected shape is visibly
// different from its resting color regardless of which color it's drawn in.
const SELECTION_HIGHLIGHT_COLOR = "#00ff00";
// 3x Cornerstone's default of "1" — thin strokes are hard to see against the
// noise of a scan, and unusable on a tablet. String, to match the library's own
// style values (ToolStyle defaults are strings).
const ANNOTATION_LINE_WIDTH = "3";

let cornerstone = null;
let cornerstoneTools = null;

export default {
  name: "DicomViewer",
  props: {
    /** Presigned URL of the .dcm file. */
    link: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: "",
      noImage: false,
      exportNotice: "",
      frameIndex: 0,
      frameCount: 1,
      imageIds: [],
      activeTool: "Navigate",
      annotationHistory: [],
      activeColor: "#ffff00",
      colors: ["#ffff00", "#ff3b30", "#0a84ff", "#ffffff", "#ff9500"],
      modes: [
        { name: "Navigate", label: "Navigate", icon: "fa-hand" },
        { name: "Freehand", label: "Freehand", icon: "fa-pen" },
        { name: "Circle", label: "Circle", icon: "fa-circle" },
        { name: "Arrow", label: "Arrow", icon: "fa-arrow-right" },
      ],
    };
  },
  computed: {
    /** Frame-scoped: an annotation on a frame the user isn't viewing can't be undone from here. */
    canUndo() {
      const currentImageId = this.imageIds[this.frameIndex];
      if (!currentImageId || !this.annotationHistory.length) return false;
      return this.annotationHistory.some(
        (entry) => entry.referencedImageId === currentImageId
      );
    },
    hintText() {
      return this.activeTool === "Navigate"
        ? "Drag to adjust brightness/contrast · scroll to zoom · right-drag to pan"
        : "Drag or touch to draw · click a shape + Delete to erase it · Ctrl+Z to undo";
    },
  },
  async mounted() {
    window.addEventListener("keydown", this.onKeydown);
    await this.setup();
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    clearTimeout(this.exportNoticeTimer);
    this.teardown();
  },
  methods: {
    async setup() {
      try {
        await this.loadLibraries();
        const imageIds = await this.buildImageIds();
        this.frameCount = imageIds.length;
        this.imageIds = imageIds;

        this.renderingEngine = new cornerstone.RenderingEngine(
          RENDERING_ENGINE_ID
        );
        this.renderingEngine.enableElement({
          viewportId: VIEWPORT_ID,
          type: cornerstone.Enums.ViewportType.STACK,
          element: this.$refs.viewport,
        });

        this.viewport = this.renderingEngine.getViewport(VIEWPORT_ID);
        await this.viewport.setStack(imageIds, 0);
        this.attachTools();
        this.viewport.render();
        this.loading = false;
      } catch (err) {
        this.loading = false;
        // The error text carries the presigned URL; strip the query string
        // (the signature) before logging.
        const detail = String((err && err.message) || err).replace(
          /\?\S*/g,
          ""
        );
        console.error("[DicomViewer] the scan could not be displayed:", detail);

        // A valid DICOM carrying no pixel data (an RT plan, a structured
        // report, a wrapped PDF) fails to load exactly like a damaged one, so
        // the failure alone cannot say which this was — the bytes can.
        this.noImage = (await this.classify()) === DICOM_NO_IMAGE;
        if (!this.noImage) {
          this.error =
            "This file could not be read as a medical image. It may not contain a scan, or it may be damaged.";
        }
      }
    },

    /**
     * Which kind of file this was, read from the file itself.
     *
     * Only ever reached after a failed load, so the path that renders a scan
     * normally still downloads the study exactly once. A pixel-less DICOM is
     * a header and nothing else, which is the case this mostly serves.
     */
    async classify() {
      try {
        const response = await fetch(this.link);
        if (!response.ok) return DICOM_UNREADABLE;
        return inspectDicom(await response.arrayBuffer());
      } catch (err) {
        return DICOM_UNREADABLE;
      }
    },

    /** Loaded on demand: a user who never opens a scan never downloads it. */
    async loadLibraries() {
      if (cornerstone && cornerstoneTools) return;

      const [core, loader, tools] = await Promise.all([
        import("@cornerstonejs/core"),
        import("@cornerstonejs/dicom-image-loader"),
        import("@cornerstonejs/tools"),
      ]);

      cornerstone = core;
      cornerstoneTools = tools;

      await core.init();
      loader.init({ maxWebWorkers: 1 });
      await tools.init();
    },

    /**
     * One image id per frame; a single image resolves to a stack of one.
     *
     * The base image must load first — the loader only publishes frame
     * metadata once it has parsed the file.
     */
    async buildImageIds() {
      const baseId = `wadouri:${this.link}`;
      await this.loadAndCache(baseId);

      const frameImageIds = cornerstone.metaData.getTyped(
        cornerstone.Enums.MetadataModules.FRAME_IMAGE_IDS,
        baseId
      );

      return frameImageIds && frameImageIds.size
        ? Array.from(frameImageIds)
        : [baseId];
    },

    /**
     * `imageLoader.loadAndCacheImage()`, but with a rejection handler attached.
     *
     * Cornerstone hands the load promise to the cache unhandled, so an
     * undecodable file leaves an orphaned rejection the browser reports as an
     * uncaught error — on top of the failure we already surface in setup().
     */
    loadAndCache(imageId) {
      const cached = cornerstone.cache.getImageLoadObject(imageId);
      if (cached) return cached.promise;

      const imageLoadObject = {
        promise: cornerstone.imageLoader.loadImage(imageId),
      };

      cornerstone.cache
        .putImageLoadObject(imageId, imageLoadObject)
        .catch(() => {
          // Reported to the user via the promise returned below.
        });

      return imageLoadObject.promise;
    },

    attachTools() {
      const {
        ToolGroupManager,
        WindowLevelTool,
        ZoomTool,
        PanTool,
        StackScrollTool,
        PlanarFreehandROITool,
        CircleROITool,
        ArrowAnnotateTool,
        Enums,
        addTool,
      } = cornerstoneTools;

      const drawingTools = [
        PlanarFreehandROITool,
        CircleROITool,
        ArrowAnnotateTool,
      ];
      const allTools = [
        WindowLevelTool,
        ZoomTool,
        PanTool,
        StackScrollTool,
        ...drawingTools,
      ];
      allTools.forEach(addTool);

      ToolGroupManager.destroyToolGroup(TOOL_GROUP_ID);
      const toolGroup = ToolGroupManager.createToolGroup(TOOL_GROUP_ID);
      allTools.forEach((tool) => {
        // ArrowAnnotateTool normally prompts for a text label and deletes
        // the arrow if left empty — skip that so it behaves like Circle/Freehand.
        if (tool === ArrowAnnotateTool) {
          toolGroup.addTool(tool.toolName, {
            getTextCallback: (doneChangingTextCallback) =>
              doneChangingTextCallback(" "),
            changeTextCallback: (_data, _eventData, doneChangingTextCallback) =>
              doneChangingTextCallback(" "),
          });
        } else {
          toolGroup.addTool(tool.toolName);
        }
      });

      // Left drag is windowing: the control a clinician reaches for first.
      toolGroup.setToolActive(WindowLevelTool.toolName, {
        bindings: [{ mouseButton: Enums.MouseBindings.Primary }],
      });
      toolGroup.setToolActive(PanTool.toolName, {
        bindings: [{ mouseButton: Enums.MouseBindings.Secondary }],
      });
      toolGroup.setToolActive(ZoomTool.toolName, {
        bindings: [{ mouseButton: Enums.MouseBindings.Wheel }],
      });
      // Drawing tools start Passive; only one tool may own Primary at a time.
      drawingTools.forEach((tool) => toolGroup.setToolPassive(tool.toolName));
      toolGroup.addViewport(VIEWPORT_ID, RENDERING_ENGINE_ID);

      this.toolGroup = toolGroup;
      this.mouseBindings = Enums.MouseBindings;
      this.toolNameByMode = {
        Navigate: WindowLevelTool.toolName,
        Freehand: PlanarFreehandROITool.toolName,
        Circle: CircleROITool.toolName,
        Arrow: ArrowAnnotateTool.toolName,
      };
      this.drawingToolNames = drawingTools.map((tool) => tool.toolName);

      // Seed the color before any stroke exists, or the in-progress drag preview is unstyled.
      this.applyDefaultColorStyle();
      this.attachAnnotationHistory();
    },

    applyDefaultColorStyle() {
      cornerstoneTools.annotation.config.style.setViewportToolStyles(
        VIEWPORT_ID,
        { global: this.annotationStyleFor(this.activeColor) }
      );
    },

    // setToolActive() merges bindings rather than replacing them, so the
    // outgoing tool must be explicitly demoted first.
    setActiveTool(mode) {
      if (mode === this.activeTool || !this.toolGroup) return;

      const outgoingToolName = this.toolNameByMode[this.activeTool];
      const incomingToolName = this.toolNameByMode[mode];
      if (!outgoingToolName || !incomingToolName) return;

      this.toolGroup.setToolPassive(outgoingToolName);

      const bindings = [{ mouseButton: this.mouseBindings.Primary }];
      if (mode !== "Navigate") bindings.push({ numTouchPoints: 1 });
      this.toolGroup.setToolActive(incomingToolName, { bindings });

      this.activeTool = mode;
    },

    // ANNOTATION_COMPLETED fires once a stroke finishes; ANNOTATION_ADDED can fire mid-draw.
    attachAnnotationHistory() {
      const drawingToolNames = new Set(this.drawingToolNames);
      const { Events } = cornerstoneTools.Enums;

      this.onAnnotationCompleted = (evt) => {
        const annotation = evt.detail.annotation;
        if (!drawingToolNames.has(annotation.metadata.toolName)) return;

        // Bake in the draw-time color so a later color-picker change doesn't repaint existing shapes.
        cornerstoneTools.annotation.config.style.setAnnotationStyles(
          annotation.annotationUID,
          this.completedAnnotationStyleFor(this.activeColor)
        );

        // The pointer rests on the shape at mouse-up, which selects it — deselect so
        // the next color-picker click doesn't silently repaint this shape too.
        cornerstoneTools.annotation.selection.deselectAnnotation(
          annotation.annotationUID
        );

        this.annotationHistory.push({
          annotationUID: annotation.annotationUID,
          referencedImageId: annotation.metadata.referencedImageId,
        });
      };

      this.onAnnotationRemoved = (evt) => {
        const annotationUID = evt.detail.annotation.annotationUID;
        const index = this.annotationHistory.findIndex(
          (entry) => entry.annotationUID === annotationUID
        );
        if (index !== -1) this.annotationHistory.splice(index, 1);
      };

      cornerstone.eventTarget.addEventListener(
        Events.ANNOTATION_COMPLETED,
        this.onAnnotationCompleted
      );
      cornerstone.eventTarget.addEventListener(
        Events.ANNOTATION_REMOVED,
        this.onAnnotationRemoved
      );
    },

    /** Removes only the current frame's drawings — other frames are untouched. */
    clearAnnotations() {
      if (!this.viewport) return;
      const element = this.$refs.viewport;
      const currentImageId = this.viewport.getCurrentImageId();
      const { state } = cornerstoneTools.annotation;

      this.drawingToolNames.forEach((toolName) => {
        const annotations = state.getAnnotations(toolName, element) || [];
        annotations
          .filter((a) => a.metadata.referencedImageId === currentImageId)
          .forEach((a) => state.removeAnnotation(a.annotationUID));
      });

      this.viewport.render();
    },

    /** Undoes the most recent stroke on the currently displayed frame only. */
    undoLast() {
      if (!this.viewport || !this.annotationHistory.length) return;
      const currentImageId = this.viewport.getCurrentImageId();

      for (let i = this.annotationHistory.length - 1; i >= 0; i--) {
        if (this.annotationHistory[i].referencedImageId === currentImageId) {
          cornerstoneTools.annotation.state.removeAnnotation(
            this.annotationHistory[i].annotationUID
          );
          this.viewport.render();
          return;
        }
      }
    },

    // Composites the canvas with the annotation overlay; the original DICOM is never touched.
    async exportSnapshot() {
      if (!this.viewport) return;
      try {
        const pixelCanvas = this.viewport.getCanvas();
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = pixelCanvas.width;
        exportCanvas.height = pixelCanvas.height;
        const ctx = exportCanvas.getContext("2d");
        ctx.drawImage(pixelCanvas, 0, 0);

        const svgLayer = this.getAnnotationSvgLayer();
        if (svgLayer) {
          const svgImage = await this.rasterizeSvg(
            svgLayer,
            exportCanvas.width,
            exportCanvas.height
          );
          ctx.drawImage(
            svgImage,
            0,
            0,
            exportCanvas.width,
            exportCanvas.height
          );
        }

        const filename = `scan-frame-${this.frameIndex + 1}.png`;
        const blob = await this.canvasToBlob(exportCanvas);
        await this.saveImage(blob, filename);
      } catch (err) {
        console.error("[DicomViewer] snapshot export failed:", err);
        this.showExportNotice("Couldn't save the image — please try again.");
      }
    },

    canvasToBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) =>
            blob
              ? resolve(blob)
              : reject(new Error("canvas.toBlob() was empty")),
          "image/png"
        );
      });
    },

    /**
     * iOS Safari ignores the `download` attribute and blocks top-level navigation to
     * `data:` URLs, so the original anchor approach silently did nothing on iPhone.
     * Straight download everywhere it works, which is everywhere except iOS. The
     * share sheet is reserved for iOS alone — offering it on desktop turns a
     * one-click save into "pick an app", which is not what the button promises.
     */
    async saveImage(blob, filename) {
      // Object URL rather than a data URL: no length ceiling, and revocable.
      const objectUrl = URL.createObjectURL(blob);
      // Deferred: revoking immediately can cancel the download mid-flight.
      const scheduleRevoke = () =>
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

      if (!this.isIOS()) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        // Safari won't dispatch a synthetic click on a detached node.
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        scheduleRevoke();
        return;
      }

      // iOS only: `download` is ignored, so the share sheet is the one route that
      // reaches Photos/Files. Falling back to a tab the user can long-press.
      try {
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file] });
          return;
        }
        window.open(objectUrl, "_blank");
        this.showExportNotice("Press and hold the image to save it.");
      } catch (err) {
        // Dismissing the share sheet is a normal user action, not a failure.
        if (err?.name !== "AbortError") {
          console.warn("[DicomViewer] share sheet unavailable:", err);
          window.open(objectUrl, "_blank");
          this.showExportNotice("Press and hold the image to save it.");
        }
      } finally {
        scheduleRevoke();
      }
    },

    /**
     * iOS is the only platform that ignores the `download` attribute, and it can't be
     * feature-detected — `'download' in HTMLAnchorElement.prototype` is true there too.
     */
    isIOS() {
      const ua = navigator.userAgent;
      if (/iPad|iPhone|iPod/.test(ua)) return true;
      // iPadOS 13+ reports itself as macOS; a real Mac has no touch points.
      return /Mac/.test(ua) && navigator.maxTouchPoints > 1;
    },

    showExportNotice(message) {
      this.exportNotice = message;
      clearTimeout(this.exportNoticeTimer);
      this.exportNoticeTimer = setTimeout(() => {
        this.exportNotice = "";
      }, 6000);
    },

    /** Mirrors Cornerstone3D's own internal SVG-layer lookup (getSvgDrawingHelper). */
    getAnnotationSvgLayer() {
      const internalDiv =
        this.$refs.viewport?.querySelector(".viewport-element");
      return internalDiv?.querySelector(":scope > .svg-layer") || null;
    },

    rasterizeSvg(svgElement, width, height) {
      return new Promise((resolve, reject) => {
        const clone = svgElement.cloneNode(true);
        clone.setAttribute("width", width);
        clone.setAttribute("height", height);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");

        const svgString = new XMLSerializer().serializeToString(clone);
        const svgDataUrl = `data:image/svg+xml;base64,${window.btoa(
          unescape(encodeURIComponent(svgString))
        )}`;

        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = svgDataUrl;
      });
    },

    onKeydown(event) {
      if (!this.viewport || this.isEditableTarget(event.target)) return;

      if (
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        event.key.toLowerCase() === "z"
      ) {
        event.preventDefault();
        this.undoLast();
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        this.deleteSelectedAnnotations();
        return;
      }

      if (this.frameCount < 2) return;
      const step =
        event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
      if (!step) return;

      event.preventDefault();
      const next = Math.min(
        Math.max(this.frameIndex + step, 0),
        this.frameCount - 1
      );
      if (next === this.frameIndex) return;

      this.frameIndex = next;
      this.viewport.setImageIdIndex(next);
    },

    /** Don't hijack Ctrl+Z/Delete from an unrelated text field elsewhere on the page. */
    isEditableTarget(target) {
      if (!target) return false;
      return (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      );
    },

    // Deletes whatever's selected — clicking an annotation selects it by default.
    deleteSelectedAnnotations() {
      const selectedUIDs =
        cornerstoneTools.annotation.selection.getAnnotationsSelected();
      if (!selectedUIDs.length) return;

      selectedUIDs.forEach((uid) =>
        cornerstoneTools.annotation.state.removeAnnotation(uid)
      );
      this.viewport.render();
    },

    // A shape is `highlighted` for its whole in-progress drag, so this covers the live
    // preview of whatever's being drawn right now — it has no per-annotation style yet.
    annotationStyleFor(color) {
      return {
        color,
        colorHighlighted: color,
        colorSelected: color,
        colorLocked: color,
        lineWidth: ANNOTATION_LINE_WIDTH,
        // Measurements are deliberately hidden. The ROI text box is the only thing
        // this flag controls, and Cornerstone reports Area/Radius in `px` whenever the
        // DICOM carries no PixelSpacing tag — an uncalibrated number that still looks
        // authoritative next to a scan. Flip to `true` to bring measurements back.
        textBoxVisibility: false,
      };
    },

    // For an already-completed shape. colorHighlighted/colorSelected must be set
    // explicitly here — omitting them falls through to the viewport-wide default
    // (which matches the active color for the live-draw case below), not to the
    // library's own default, so there'd be no visible sign a shape is selected.
    completedAnnotationStyleFor(color) {
      return {
        color,
        colorLocked: color,
        colorHighlighted: SELECTION_HIGHLIGHT_COLOR,
        colorSelected: SELECTION_HIGHLIGHT_COLOR,
        lineWidth: ANNOTATION_LINE_WIDTH,
        textBoxVisibility: false,
      };
    },

    setColor(color) {
      this.activeColor = color;
      if (!cornerstoneTools || !this.viewport) return;

      this.applyDefaultColorStyle();

      const { style } = cornerstoneTools.annotation.config;
      cornerstoneTools.annotation.selection
        .getAnnotationsSelected()
        .forEach((uid) =>
          style.setAnnotationStyles(
            uid,
            this.completedAnnotationStyleFor(color)
          )
        );

      this.viewport.render();
    },

    reset() {
      if (!this.viewport) return;
      this.viewport.resetCamera();
      this.viewport.resetProperties();
      this.viewport.render();
    },

    teardown() {
      try {
        if (cornerstoneTools) {
          cornerstoneTools.ToolGroupManager.destroyToolGroup(TOOL_GROUP_ID);
          const { Events } = cornerstoneTools.Enums;
          if (this.onAnnotationCompleted) {
            cornerstone.eventTarget.removeEventListener(
              Events.ANNOTATION_COMPLETED,
              this.onAnnotationCompleted
            );
          }
          if (this.onAnnotationRemoved) {
            cornerstone.eventTarget.removeEventListener(
              Events.ANNOTATION_REMOVED,
              this.onAnnotationRemoved
            );
          }
        }
        if (this.renderingEngine) this.renderingEngine.destroy();
      } catch {
        // A failed teardown must not break closing the modal.
      }
      this.renderingEngine = null;
      this.viewport = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.dicom-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
}

.dicom-viewport {
  flex: 1;
  width: 100%;
  min-height: 0;
  touch-action: none; // prevents iOS Safari's scroll/pinch from fighting drawing

  &.is-hidden {
    visibility: hidden;
  }
}

.dicom-viewer-message {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  text-align: center;
  padding: 24px;
  z-index: 2;
}

.dicom-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px 16px;
  background: #11161d;
  color: #cbd5e1;
  font-size: 12px;
  flex-shrink: 0;

  .dicom-mode-group,
  .dicom-action-group {
    display: flex;
    gap: 4px;
  }

  .dicom-color-group {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dicom-color-swatch {
    width: 18px;
    height: 18px;
    padding: 0;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;

    &.is-active {
      border-color: #fff;
    }
  }

  .dicom-hint {
    flex: 1;
    opacity: 0.75;
  }

  .dicom-frame-counter {
    font-variant-numeric: tabular-nums;
  }

  .dicom-export-notice {
    flex-basis: 100%;
    color: #ffd166;
  }
}
</style>
